import { PermissionProtectedComponent } from 'auth/PermissionProtectedComponent';
import { Accordion } from 'components/Accordion/Accordion';
import { Page } from 'layouts/Admin/Page';
import { ReactNode, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Entry, sections } from './FaqSections';

export const FaqView = () => {
  const navigate = useNavigate();
  const { entry } = useParams<{ entry: string }>();
  const [openEntry, setOpenEntry] = useState<string | null>(entry ?? null);

  const onOpenEntry = (entry: Entry) => {
    setOpenEntry((prev) => (prev === entry.slug ? null : entry.slug));
    navigate(`/faq/${entry.slug}`);
  };

  return (
    <Page title="Smart Video - FAQ" breadcrumbs={[{ name: 'FAQ' }]}>
      <div className="flex flex-col">
        <div className="flex flex-col gap-3">
          <h1 className="text-lg font-bold">Ofte stillede spørgsmål</h1>
          <p>
            Her finder du svar på de mest almindelige spørgsmål om Smart Create.
          </p>
          <p>
            Hvis du har spørgsmål, feedback eller skulle opleve udfordringer, er
            du velkommen til at oprette en{' '}
            <Link
              className="text-primary underline"
              to="/organization/service-tickets"
            >
              supportsag
            </Link>
            , så hjælper vi dig videre.
          </p>
          <FaqTableOfContents openEntry={openEntry} onOpenEntry={onOpenEntry} />
          <div className="divider"></div>
        </div>

        <div className="flex flex-col gap-6">
          {sections.map((section) => {
            if (!section.entries.length) return null;

            return (
              <PermissionProtectedComponent
                key={section.title}
                permissions={section.permissions}
              >
                <FaqSection
                  title={section.title}
                  description={section.description}
                >
                  <div className="flex flex-col gap-2">
                    {section.entries.map((entry) => (
                      <PermissionProtectedComponent
                        key={entry.title}
                        permissions={entry.permissions}
                      >
                        <Accordion
                          key={entry.title}
                          title={entry.title}
                          defaultOpen={openEntry === entry.slug}
                          onOpen={() => onOpenEntry(entry)}
                        >
                          {entry.content}
                        </Accordion>
                      </PermissionProtectedComponent>
                    ))}
                  </div>
                </FaqSection>
              </PermissionProtectedComponent>
            );
          })}
        </div>
      </div>
    </Page>
  );
};

interface FaqSectionProps {
  title: string;
  description?: string;
  children: ReactNode;
}

const FaqSection = ({ title, description, children }: FaqSectionProps) => {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      {description ? <p className="mb-2">{description}</p> : null}
      <div>{children}</div>
    </div>
  );
};

interface FaqTableOfContentsProps {
  openEntry: string | null;
  onOpenEntry: (entry: Entry) => void;
}

const FaqTableOfContents = ({
  openEntry,
  onOpenEntry,
}: FaqTableOfContentsProps) => {
  return null;
  return (
    <div className="card bg-base-200 shadow-xl p-4">
      <h2 className="text-xl font-bold text-primary mb-4">
        Indholdsfortegnelse
      </h2>
      <ul className="space-y-4">
        {sections
          .filter((section) => section.entries.length)
          .map((section) => (
            <li key={section.title}>
              <h3 className="font-semibold">{section.title}</h3>
              <ul className="menu menu-vertical ps-2">
                {section.entries.map((entry) => (
                  <li key={entry.slug}>
                    <Link
                      to={`/faq/${entry.slug}`}
                      className={`text-sm ${
                        entry.slug === openEntry
                          ? 'text-primary font-medium'
                          : 'text-primary'
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        onOpenEntry(entry);
                      }}
                    >
                      {entry.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
      </ul>
    </div>
  );
};
